package dev.lingvo.site.components.review

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.components.Picker
import dev.lingvo.site.components.PickerRow
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LProgressBar
import dev.lingvo.site.ds.LProgressCounts
import dev.lingvo.site.ds.LSegmented
import dev.lingvo.site.ds.LSegmentedOption
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectRouteContext
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** One language a reviewer can switch to without leaving the lane. */
internal class ReviewLocaleOption(
    val code: String,
    val label: String,
    val path: String,
    val current: Boolean,
)

/**
 * Identity, task and state for the review lane, in that order.
 *
 * The language is the lane's working axis, so it is a picker rather than a word: moving from German
 * to French used to mean leaving the queue, finding the project overview and coming back through a
 * readiness row. The QA-clean count is the header's status because it is what sizes the one
 * automation this page offers, and the progress bar is state — how far this sitting has got — which
 * is why it sits under the subtitle rather than beside the title.
 */
@Composable
internal fun ReviewHeader(
    title: String,
    subtitle: String,
    locale: String,
    language: String,
    localeOptions: List<ReviewLocaleOption>,
    qaCleanPending: Int,
    decided: Int,
    queueTotal: Int,
    actions: (@Composable () -> Unit)? = null,
    qaCleanIsFloor: Boolean = false,
) {
    PageHeader(
        title = title,
        subtitle = subtitle,
        // No mark. `PageHeader` lays the mark and the text side by side with `Space.MD` between
        // them, so a 24px glyph bought the title, the status pill, the subtitle and the whole meta
        // row a 36px indent while the banner, the cards and the batch button below stayed flush
        // with the content column — two left edges on one page, the heavier one wrong. The eyebrow
        // locale picker and "Review German" already state whose queue this is.
        mark = null,
        eyebrow = if (localeOptions.size >= 2) {
            { ReviewLocalePicker(locale, language, localeOptions) }
        } else {
            null
        },
        status = if (qaCleanPending > 0) {
            {
                LBadge(
                    if (qaCleanIsFloor) tPlural(
                        "review.review_header.at_least_count_qa_clean",
                        qaCleanPending,
                        "At least {count} QA-clean",
                        "At least {count} QA-clean",
                    ) else tPlural(
                        "review.review_header.count_qa_clean",
                        qaCleanPending,
                        "{count} QA-clean",
                        "{count} QA-clean",
                    ),
                    variant = LBadgeVariant.SUCCESS,
                    dot = true,
                )
            }
        } else {
            null
        },
        meta = if (queueTotal > 0) {
            { ReviewProgressMeta(decided, queueTotal, language) }
        } else {
            null
        },
        actions = actions,
    )
}

/** How far this sitting has got through the queue it started with. */
@Composable
private fun ReviewProgressMeta(decided: Int, queueTotal: Int, language: String) {
    // Clamped rather than trusted: the queue is a live set, and somebody else submitting into it
    // while this reviewer works can leave more decided than the sitting started with.
    val total = maxOf(queueTotal, decided)
    // 320px shared between a `width: 100%` bar and a label that was allowed to wrap left the bar
    // about 148px for twenty fixed-gap cells — a dotted rule rather than a bar — and broke "0 of 12
    // decided" onto two lines starting 130px right of everything else in the header. The cap is
    // wider, the bar has a floor, and the numeral is one line or none.
    Row(
        Modifier.gap(Space.SM).maxWidth(420.px),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        LProgressBar(
            LProgressCounts(total = total, translated = 0, approved = decided.coerceAtMost(total)),
            modifier = Modifier.minWidth(180.px),
            label = trf(
                "review.review_header.review_progress_in_language",
                "Review progress in {language}",
                "language" to language,
            ),
            compact = true,
        )
        Span(Modifier.whiteSpace(WhiteSpace.NoWrap).toAttrs()) {
            Text(
                tf(
                    "review.review_header.done_of_total_decided",
                    "{done} of {total} decided",
                    "done" to decided,
                    "total" to total,
                ),
            )
        }
    }
}

/** The lane's working axis, as real anchors somebody can middle-click. */
@Composable
private fun ReviewLocalePicker(
    locale: String,
    language: String,
    options: List<ReviewLocaleOption>,
) {
    var open by remember(locale) { mutableStateOf(false) }
    Picker(
        label = language,
        ariaLabel = trf(
            "review.review_header.switch_language_currently_language",
            "Switch language, currently {language}",
            "language" to language,
        ),
        idSeed = "review-locale-$locale",
        open = open,
        onOpenChange = { open = it },
        labelLang = locale,
        // The eyebrow, the H1 under it and the banner above it share one left edge. Without this
        // the picker's own inline padding started "German" 8px right of "Review German".
        flushStart = true,
    ) {
        options.forEach { option ->
            // Closing on activation is the popover's own job — its container closes on any click
            // inside, since a row is a link and navigation follows.
            PickerRow(
                label = option.label,
                href = option.path,
                current = option.current,
                onActivate = {},
                labelLang = option.code,
            )
        }
    }
}

/**
 * The scope control, offering only the jobs this queue actually contains.
 *
 * **"On this screen", not "waiting".** `ReviewRepo.queue` returns at most one screenful while
 * `total` counts the whole pending set, so on a queue of 180 the header said 180 and this control
 * said 25 — two numbers for one set, eight pixels apart.
 *
 * An option that can only ever empty the queue is a control that lies about what is waiting, so a
 * scope appears only when something is in it and something else is not.
 */
@Composable
internal fun ReviewScopeControl(
    scope: ReviewScope,
    onScopeChange: (ReviewScope) -> Unit,
    loaded: Int,
    revisedCount: Int,
    qaCleanCount: Int,
) {
    val options = buildList {
        add(
            LSegmentedOption(
                ReviewScope.ALL,
                trf(
                    "review.review_header.all_on_this_screen",
                    "All on this screen ({count})",
                    "count" to loaded,
                ),
            ),
        )
        if (revisedCount in 1 until loaded) {
            add(
                LSegmentedOption(
                    ReviewScope.REVISED,
                    trf(
                        "review.review_header.changed_since_approval_option",
                        "Changed since approval ({count})",
                        "count" to revisedCount,
                    ),
                ),
            )
        }
        if (qaCleanCount in 1 until loaded) {
            add(
                LSegmentedOption(
                    ReviewScope.QA_CLEAN,
                    trf(
                        "review.review_header.qa_clean_only_option",
                        "QA-clean only ({count})",
                        "count" to qaCleanCount,
                    ),
                ),
            )
        }
    }
    if (options.size < 2) return
    LSegmented(
        options = options,
        selected = scope,
        onSelected = onScopeChange,
        label = t("review.review_header.review_scope", "Review scope"),
    )
}

/**
 * The other languages of this project, when the verified route already knows them.
 *
 * Read from the route cache the layout filled rather than fetched: a second request for a list the
 * page already has would be a second chance for the two to disagree, and a picker is not worth one.
 */
@Composable
internal fun reviewLocaleOptions(slug: String, locale: String): List<ReviewLocaleOption> {
    val project = ProjectRouteContext.current(slug)
        ?.takeIf { it.project.slug == slug }
        ?.project
        ?: return emptyList()
    return project.locales
        .filter { it != project.sourceLocale }
        .map { code ->
            ReviewLocaleOption(
                code = code,
                label = localeInfo(code).englishName,
                path = "/app/projects/$slug/review/$code",
                current = code == locale,
            )
        }
}

/**
 * The line under "Review German", as one sentence per case.
 *
 * **Whole messages rather than halves and a separator.** This was built by appending
 * " · 3 of 12 on this screen" to a count, so a translator received the count and the position as
 * unrelated fragments with the middot and the joining kept in Kotlin. A language that puts the
 * position first, or separates clauses differently, could express neither.
 *
 * The counts stay plural forms: "1 waiting" is a different sentence from "12 waiting" in most
 * languages, and this is the number a reviewer reads before deciding whether to start.
 */
internal fun reviewSubtitle(
    total: Int,
    visible: Int,
    cursor: Int,
    scope: ReviewScope,
): String {
    val positioned = cursor in 0 until visible
    return when {
        // Nothing, deliberately. The header's state line and the empty card's heading were the
        // same fact fifty pixels apart — "Nothing waiting for review in German." sitting directly
        // over "Nothing waiting for review." — so the header spent its one state line saying the
        // body's headline back to it, and the reader got one fact where two would fit. The card
        // below carries the state, the evidence (approved in the last seven days) and the one
        // invitation; the header keeps identity and task, which is the order §2.1 asks for.
        total == 0 -> ""
        scope == ReviewScope.REVISED && positioned -> tPlural(
            "review.review_header.changed_since_approval_positioned",
            visible,
            "{count} changed since approval · {index} of {size} on this screen",
            "{count} changed since approval · {index} of {size} on this screen",
            "index" to cursor + 1,
            "size" to visible,
        )
        scope == ReviewScope.REVISED -> tPlural(
            "review.review_header.changed_since_approval",
            visible,
            "{count} changed since approval",
            "{count} changed since approval",
        )
        scope == ReviewScope.QA_CLEAN && positioned -> tPlural(
            "review.review_header.qa_clean_positioned",
            visible,
            "{count} QA-clean · {index} of {size} on this screen",
            "{count} QA-clean · {index} of {size} on this screen",
            "index" to cursor + 1,
            "size" to visible,
        )
        scope == ReviewScope.QA_CLEAN -> tPlural(
            "review.review_header.qa_clean_waiting",
            visible,
            "{count} QA-clean",
            "{count} QA-clean",
        )
        positioned -> tPlural(
            "review.review_header.waiting_positioned",
            total,
            "{count} waiting · {index} of {size} on this screen",
            "{count} waiting · {index} of {size} on this screen",
            "index" to cursor + 1,
            "size" to visible,
        )
        else -> tPlural(
            "review.review_header.waiting",
            total,
            "{count} waiting",
            "{count} waiting",
        )
    }
}
